import { Injectable, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProductInCompatibility } from '@core/services/CompatibilityHandler/productInCompatibility';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import pasosBarraLateral from './pasos';
import { ArmaPc } from '@core/models/ArmaPc';
import { ArmaPcDataService } from './arma-pc-data.service';
import { ProductIndex } from './ProductIndex';
import { Product } from '@core/models/piramide/Product';
import { ArmadoRelacionesService } from '../../product/services/armado-relaciones.service';
import { DatosArmado } from '@core/models/datosArmado';
import { Paso } from './Paso';
import { DataArmado } from '@core/services/CompatibilityHandler/funcionesArmado/DataArmado';
import { FuncionesArmadoAgrupador } from '@core/services/CompatibilityHandler/funcionesArmado/FuncionesArmadoAgrupador';
import { CoolerFan } from '@core/services/CompatibilityHandler/model';
import { PiramidalService } from '@core/services/piramidal.service';
import { ModalArmadoComponent } from '../components/modal-armado/modal-armado.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationService } from '@core/services/navigation.service';
import { ModalAlertaComponent } from '../components/modal-alerta/modal-alerta.component';
import { ComponentesObligatorios } from './componentesObligatoriosArmado';
import { ProductsService } from '@core/services/products.service';
import { IncompatibilitiesType } from '@core/enums/incompatibilities-types.enum';

interface PasosUrl {
  [x: number]: string;
}
@Injectable({
  providedIn: 'root',
})
export class ArmaPcService implements OnDestroy {
  public idProductsBuild = signal<number[]>(null);
  private datosProducto = new BehaviorSubject([]);
  public datosProducto$ = this.datosProducto.asObservable();
  private productosIncompatiblesObs = new BehaviorSubject<ProductInCompatibility<Product>[]>([]);
  public productosIncompatiblesObs$ = this.productosIncompatiblesObs.asObservable();
  private pasoActual = new BehaviorSubject<Paso>(null);
  public pasoActual$ = this.pasoActual.asObservable();
  private productosFinales: BehaviorSubject<ProductInCompatibility<Product>[]> =
    new BehaviorSubject([]);
  public productosFinales$ = this.productosFinales.asObservable();
  private paso: number = 1;
  private tipo: number;
  private productsIndex: ProductIndex = {};
  private datosArmados = new BehaviorSubject(pasosBarraLateral);
  public datosArmados$ = this.datosArmados.asObservable();
  private armadoSeleccionado = new BehaviorSubject(new DataArmado());
  private params: Params = null;
  private componentesObligatorios = ComponentesObligatorios;
  private pasosUrlNuevos: PasosUrl = {
    1: 'cpu',
    2: 'mother',
    3: 'ccpu',
    4: 'mem',
    5: 'video',
    6: 'hd',
    7: 'fuente',
    8: 'gab',
    9: 'monitores',
    10: 'perifericos',
  };
  private nombres = {
    cpu: 'cpus',
    mother: 'mother',
    ccpu: 'ccpu',
    mem: 'memorias',
    video: 'placasVideos',
    hd: 'discos',
    fuente: 'fuentes',
    gab: 'gabinetes',
    monitores: 'monitores',
    perifericos: 'perifericos',
  };
  private subscription: Subscription;
  private dialogRefSubscription: Subscription;
  constructor(
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private armaPcDataService: ArmaPcDataService,
    private armadoService: ArmadoRelacionesService,
    private funcionesArmadoAgrupador: FuncionesArmadoAgrupador,
    private piramidalService: PiramidalService,
    private dialogRef: MatDialogRef<ModalArmadoComponent>,
    private dialog: MatDialog,
    private navigationService: NavigationService,

    private _productsservice: ProductsService,
  ) {
    //se agrego el llamado extra a getDataArmaPc para alimentar los subjects y que traiga data al recargar la pagina desde productos
    //fue un fix rapido para evitar cambiar mucho codigo de arma-pc-data-service

    this.armaPcDataService.startLoading();
    this.armaPcDataService.getDataArmaPc();
    this.subscription = combineLatest([
      this.armaPcDataService.obtenerProductos$,
      this.armaPcDataService.obtenerProductosIndex$,
    ])
      .pipe(
        tap((res) => {
          this.productsIndex = { ...res[1] };
          let datosPasos = this.cargarProductosInicial(res[0], this.datosArmados.getValue());
          this.datosArmados.next(datosPasos);
        }),
        concatMap(() => this.activatedRoute.queryParams),
      )
      .subscribe((params) => {
        this.params = { ...params };
        this.tipo = parseInt(params?.tipo) || 0;
        this.paso = parseInt(params?.paso) || 1;
        //vacio el objeto al inicio xq sino toma data vieja cuando se navega x menu
        let datosPasos: DatosArmado = this.datosArmados.getValue();
        datosPasos.vaciarObjeto();
        let dataArmado: DataArmado = this.armadoSeleccionado.getValue();
        dataArmado.vaciarArmado();
        if (Object.keys(this.params)?.length) {
          let verificacion = this.verificarURL(this.params, datosPasos);
          if (verificacion) {
            this.armarUrl(this.params);
            return;
          }
          this.cargarCantidadesSeleccionadas(datosPasos);
          if (params.paso == 11) {
            let finalData = this.cargarProductosFinales(datosPasos);
            this.productosFinales.next(finalData);
          }
          this.datosArmados.next(datosPasos);
        }
        this.armarPc(datosPasos);
      });
  }

  public clearBuild(): void {
    this.datosArmados.getValue().vaciarObjeto();
    this.armadoSeleccionado.getValue().vaciarArmado();
  }

  private superFuncion(
    armado: DataArmado,
    productosAValidar: ProductInCompatibility<Product>[],
    paso: Paso,
  ): ProductInCompatibility<Product>[] {
    let productosIncompatibles: ProductInCompatibility<Product>[] = [];
    productosAValidar.forEach((producto) => {
      let incomp = this.funcionesArmadoAgrupador.ejecutar(armado, producto.product);
      if (incomp?.length) {
        let productoIncomp = new ProductInCompatibility(producto.product, 1);
        productoIncomp.incompatibilities = incomp;
        productosIncompatibles.push(productoIncomp);
      } else {
        let index = paso.productoSeleccionado.findIndex(
          (prod) => prod.product.id_producto == producto.product.id_producto,
        );
        if (index != -1) {
          paso.productoSeleccionado[index].cantidad++;
        } else {
          paso.productoSeleccionado.push(producto);
        }
        armado.cargarSuperProducto(producto.product);
      }
    });
    this.armadoSeleccionado.next(armado);
    return productosIncompatibles;
  }

  // Verifica si la url tiene ids validos
  private verificarURL(parametros: Params, datosPasos: DatosArmado): boolean {
    let dataArmado = this.armadoSeleccionado.getValue();
    dataArmado.vaciarArmado();
    let urlEditada = false;
    let productosAValidar: Array<ProductInCompatibility<Product>> = [];
    let productosBorrados: Array<ProductInCompatibility<Product>> = [];
    for (const paso in datosPasos) {
      if (parametros[paso]) {
        let arrayId: string[] = parametros[paso]
          ?.split(',')
          ?.filter((id) => this.productsIndex[paso][id]);
        if (arrayId.length) {
          if (datosPasos[paso].productoUnico) {
            arrayId = [arrayId[arrayId.length - 1]];
          }
          //creo 1 array de productIncompatibilities con los ids validos de la url
          productosAValidar = arrayId.map(
            (id) => new ProductInCompatibility(this.productsIndex[paso][id].product, 1),
          );
          //en superfuncion corro las validaciones a los productos seleccionados
          //aca recibo los productos en url que tienen incompatibilidades
          let productosIncompatibles = this.superFuncion(
            dataArmado,
            productosAValidar,
            datosPasos[paso],
          );
          //si hay productos incompatibles en el armado ,
          if (productosIncompatibles.length) {
            //los agrupo por id y los paso al array de borrados para llenar el observable que dispara el modal
            this.agruparArray(productosIncompatibles, productosBorrados);
            //los saco del array de IDs que despues le paso como param a la url
            productosIncompatibles.forEach((producto) => {
              let index = arrayId.findIndex((id) => +id == producto.product.id_producto);
              if (index != -1) {
                arrayId.splice(index, 1);
              }
            });
          }
        }
        if (parametros[paso] !== arrayId?.join(',')) {
          urlEditada = true;
        }
        parametros[paso] = arrayId?.length ? arrayId?.join(',') : null;
      }
    }
    this.datosArmados.next(datosPasos);
    //si hay productos que fueron borrados se pasan todos juntos al observable para disparar el modal 1 vez
    if (productosBorrados?.length) {
      this.productosIncompatiblesObs.next(productosBorrados);
    }
    return urlEditada;
  }

  private agruparArray(
    productosIncompatibles: Array<ProductInCompatibility<Product>>,
    productosBorrados: Array<ProductInCompatibility<Product>>,
  ): void {
    productosIncompatibles.forEach((prod) => {
      let index = productosBorrados.findIndex(
        (prodBorrados) => prodBorrados.product.id_producto == prod.product.id_producto,
      );
      if (index != -1) {
        productosBorrados[index].cantidad++;
      } else {
        productosBorrados.push(prod);
      }
    });
  }

  public getProductosFinales(): ProductInCompatibility<Product>[] {
    return this.productosFinales.getValue();
  }

  private cargarProductosInicial(productos: ArmaPc, datosArmados: DatosArmado): DatosArmado {
    for (const paso in datosArmados) {
      datosArmados[paso].lista_productos = productos[paso];
      this.filterPowerSuppliesWithCables(productos, datosArmados, paso);
    }
    return datosArmados;
  }

  /**
   * se filtran las fuentes para asegurarse de que incluyan cable
   * o que haya un cable disponible en stock.
   */
  private filterPowerSuppliesWithCables(
    productos: ArmaPc,
    datosArmados: DatosArmado,
    paso: string,
  ): void {
    if (datosArmados[paso].tipo == 'Fuente') {
      datosArmados[paso].lista_productos = productos[paso].filter(
        (p) =>
          p.product.$caracteristicas.incluye_cable ||
          this.piramidalService.products.cache[1705]?.stock > 0,
      );
    }
  }

  // se cargan las cantidades de los productos seleccionados en la lista general de productos
  private cargarCantidadesSeleccionadas(datosArmados: DatosArmado): DatosArmado {
    for (const paso in datosArmados) {
      datosArmados[paso].seleccionado = this.paso == datosArmados[paso].paso;
      datosArmados[paso].productoSeleccionado?.forEach((productoSelect) => {
        let product = datosArmados[paso].lista_productos.find(
          (p) => p.product.id_producto == productoSelect.product.id_producto,
        );
        if (product) {
          product.cantidad = productoSelect.cantidad;
        }
      });
    }
    return datosArmados;
  }

  /**
   * Esta función determina las compatibilidades de la lista de productos disponibles.
   *
   * al terminar realiza ejecutando un evento en el subject pasoActual.
   *
   */
  private armarPc(datosPasos: DatosArmado): void {
    let dataArmado: DataArmado = this.armadoSeleccionado.getValue();
    let agrupador: string = this.pasosUrlNuevos[this.paso];
    for (const paso in datosPasos) {
      if (paso == 'cpu') this.armadoService.relacionCpuFamilia(datosPasos, this.tipo);
      if (dataArmado[this.nombres[paso]]?.length || paso == agrupador) {
        this.armadoService.validarProductos(datosPasos[paso].lista_productos, dataArmado);
      }
      datosPasos[paso].lleno = datosPasos[paso].lista_productos.every((p) =>
        p.incompatibilities.some((i) => i.tipo == IncompatibilitiesType.CANTIDAD),
      );
    }
    this.pasoActual.next(datosPasos[agrupador]);
  }

  public abrirModalAlerta(
    tipo: 'CPUsinGPU' | 'ARMADOsinGPU' | 'RESTOGPUCarrito',
    config?: { ocultarBotonPrimario?: boolean; ocultarBotonSecundario?: boolean },
  ): MatDialogRef<ModalAlertaComponent> {
    return this.dialog.open(ModalAlertaComponent, {
      width: '400px',
      height: 'auto',
      maxHeight: 'auto',
      maxWidth: '90vw',
      disableClose: true,
      data: { tipo, config },
    });
  }

  public esGPURequerida(producto: Product): boolean {
    return (
      producto.$caracteristicas.chipset_gpu == null ||
      producto.$caracteristicas.chipset_gpu == '' ||
      producto.$caracteristicas.chipset_gpu.startsWith('NO Posee')
    );
  }

  public seleccionarProducto(
    producto: ProductInCompatibility<Product>,
    restar?: boolean,
    pasoActual?: Paso,
    goToStep?: number,
  ): void {
    let CPUsinGPU =
      (producto.product.id_subcategoria == 48 || producto.product.id_subcategoria == 27) &&
      this.esGPURequerida(producto.product) &&
      !this.datosArmados.getValue().video.productoSeleccionado.length &&
      !restar;

    let PCsinGPU =
      (producto.product.id_subcategoria == 6 || producto.product.id_subcategoria == 62) &&
      this.datosArmados.getValue().cpu.productoSeleccionado.length &&
      this.esGPURequerida(this.datosArmados.getValue().cpu.productoSeleccionado[0].product) &&
      restar;

    if (CPUsinGPU || PCsinGPU) {
      const dialogRef = this.abrirModalAlerta(
        'CPUsinGPU',
        restar ? { ocultarBotonSecundario: true } : undefined,
      );
      this.dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          this.agregarProducto(producto, restar, pasoActual, goToStep);
        } else if (PCsinGPU) {
          this.agregarProducto(producto, restar, pasoActual, 1);
        }
      });
    } else {
      this.agregarProducto(producto, restar, pasoActual, goToStep);
    }
  }

  public agregarProducto(
    producto: ProductInCompatibility<Product>,
    restar?: boolean,
    pasoActual?: Paso,
    iraPaso?: number,
  ): void {
    let campoUrl: string;
    let ids = '';
    let paso: number = 1;
    let acumulable = false;
    let tipo: number;
    switch (producto.product.id_subcategoria) {
      case 48:
      case 27:
        campoUrl = 'cpu';
        paso = 2;
        tipo = producto.product.id_subcategoria == 27 ? 26 : 49;
        break;
      case 49:
      case 26:
        campoUrl = 'mother';
        paso = 3;
        tipo = producto.product.id_subcategoria;
        break;
      case 36:
        campoUrl = 'ccpu';
        paso = 4;
        break;
      case 15:
      case 47:
        campoUrl = 'mem';
        acumulable = true;
        paso = 4;
        break;
      case 6:
      case 62:
      case 116:
        campoUrl = 'video';
        acumulable = true;
        paso = 5;
        break;
      case 81:
      case 19:
        campoUrl = 'hd';
        acumulable = true;
        paso = 6;
        break;
      case 34:
        campoUrl = 'fuente';
        paso = 8;
        break;
      case 7:
        campoUrl = 'gab';
        paso = 9;
        break;
      case 5:
      case 79:
        campoUrl = 'monitores';
        acumulable = true;
        paso = 9;
        break;
      case 2:
      case 38:
      case 39:
      case 8:
      case 35:
      case 52:
        campoUrl = 'perifericos';
        acumulable = true;
        paso = 10;
        break;
    }
    if (producto.product.id_producto == 2093 || producto.product.id_producto == 2094) {
      campoUrl = 'ccpu';
      paso = 4;
    }
    if (!pasoActual.productoUnico && !restar && !(producto.product instanceof CoolerFan)) {
      let dataArmado: DataArmado = this.armadoSeleccionado.getValue();
      dataArmado[this.nombres[campoUrl]].push(producto.product);
      let incompatibilidades = this.funcionesArmadoAgrupador.ejecutar(dataArmado, producto.product);
      paso =
        (
          incompatibilidades.some(
            (i) =>
              i.tipo == IncompatibilitiesType.CANTIDAD ||
              i.tipo == IncompatibilitiesType.DEFAULT_STOCK,
          )
        ) ?
          paso + 1
        : paso;
    }
    if (restar && pasoActual.productoUnico) {
      paso--;
    }
    if (paso > this.paso) {
      this.goToTop();
    }
    ids = this.definirCantidad(this.params?.[campoUrl], producto, restar, acumulable);

    let parametros = {
      [campoUrl]: ids ? ids : null,
      paso: paso,
    };
    if (producto.product?.$caracteristicas?.cant_cooler >= 3 && !restar) this.abrirModalPlaca();
    if (iraPaso) {
      parametros.paso = iraPaso;
    }
    this.armarUrl(parametros, tipo);
  }

  public limitQuantityProductParams(product: ProductInCompatibility<Product>): void {
    let idParam;
    switch (product.product.id_subcategoria) {
      case 47:
        idParam = 'mem';
        break;
      case 6:
      case 62:
        idParam = 'video';
        break;
      case 81:
      case 19:
        idParam = 'hd';
        break;
      case 5:
      case 79:
        idParam = 'monitores';
        break;
      case 2:
      case 38:
      case 39:
      case 8:
      case 35:
      case 52:
        idParam = 'perifericos';
        break;
    }
    if (!idParam) return;

    let paramValues = [];
    for (let index = 0; index < product.cantidad; index++) {
      paramValues.push(product.product.id_producto);
    }
    const restOfIds = this.params?.[idParam]
      ?.split(',')
      .filter((idProd) => idProd != product.product.id_producto)
      .join(',');
    const parameters = {
      [idParam]: paramValues.join(',') + restOfIds ? ',' + restOfIds : '',
    };
    this.armarUrl(parameters);
  }

  private abrirModalPlaca(): void {
    this.dialogRef = this.dialog.open(ModalArmadoComponent, {
      width: '350px',
      height: 'auto',
      maxHeight: 'auto',
      maxWidth: '100vw',
      disableClose: true,
      data: { producto: null, edicion: false, alertaPlaca: true },
    });
  }

  private goToTop(finCompra?: boolean): void {
    const element = document.querySelector('mat-sidenav-content') || window;
    const behavior = finCompra ? 'smooth' : 'auto';
    const obj: any = { top: 0, left: 0, behavior };
    element.scroll(obj);
  }

  /**
   * suma o resta un id al conjunto de ids seleccionados en los parametros de la url
   * y retorna un string con los ids que quedaron, separados por coma.
   * @param params string de id_producto seleccionados separados por coma
   * @param producto el producto seleccionado
   * @param restar si se resta un id, pasar true
   */
  private definirCantidad(
    params: string,
    producto: ProductInCompatibility<Product>,
    restar: boolean,
    acumulable: boolean,
  ): string {
    if (!params) return `${producto.product.id_producto}`;
    let arrayId = params.split(',');
    if (restar) {
      let index = arrayId.findIndex((id) => +id == producto.product.id_producto);
      if (index > -1) {
        arrayId.splice(index, 1);
      }
    } else {
      if (!acumulable) return `${producto.product.id_producto}`;
      arrayId.push(`${producto.product.id_producto}`);
    }
    return arrayId.length > 1 ? arrayId.join(',') : arrayId.toString();
  }

  public validarComponentesObligatorios(datosArmados: DatosArmado): boolean {
    let valido: boolean = false;
    for (const componente in this.componentesObligatorios) {
      for (const paso in datosArmados) {
        if (
          datosArmados[paso].productoSeleccionado.some(
            (prod) => prod.product instanceof this.componentesObligatorios[componente],
          )
        ) {
          valido = true;
          break;
        } else {
          valido = false;
        }
      }
      if (!valido) break;
    }
    return valido;
  }

  public esObligatorio(producto: Product): boolean {
    for (const componente in this.componentesObligatorios) {
      if (producto instanceof this.componentesObligatorios[componente]) {
        return true;
      }
    }
    return false;
  }

  public volverAtrasUrl(paso: number): void {
    if (paso == 1) return;
    let nuevoPaso: number = paso - 1;
    this.paso = nuevoPaso;
    let parametros = {
      paso: nuevoPaso,
    };
    this.goToTop();
    this.armarUrl(parametros);
  }

  public armarUrl(parametros: { paso?: number; tipo?: number }, tipo?: number): void {
    if (tipo) {
      parametros.tipo = tipo;
    }
    this.navigationService.navigate(['/armatupc'], {
      queryParams: parametros,
      queryParamsHandling: 'merge',
    });
  }

  public limpiarUrl(): void {
    const dataProducts = this.datosArmados.getValue();
    dataProducts.vaciarObjeto();
    this.datosArmados.next(dataProducts);
    let dataArmado = this.armadoSeleccionado.getValue();
    dataArmado.vaciarArmado();
    this.paso = 1;
    this.goToTop();
    this.route.navigate(['armatupc'], {
      queryParams: null,
    });
  }

  //Indica la suma de watts que va el armado
  public getWatts(): number {
    let dataProducts = this.datosArmados.getValue();
    let suma = 0;
    for (const product in dataProducts) {
      if (dataProducts[product].productoSeleccionado?.length) {
        dataProducts[product].productoSeleccionado.forEach((producto) => {
          suma += (producto.product.$caracteristicas?.w ?? 0) * producto.cantidad;
        });
      }
    }
    return suma;
  }

  public getPrice(dataArmado?: DatosArmado): number {
    let dataProducts = dataArmado ? dataArmado : this.datosArmados.getValue();
    let sumaPrecio = 0;
    for (const product in dataProducts) {
      if (dataProducts[product].productoSeleccionado?.length) {
        dataProducts[product].productoSeleccionado.forEach((producto) => {
          sumaPrecio += (producto.product.precioEspecial ?? 0) * producto.cantidad;
        });
      }
    }
    return sumaPrecio;
  }

  public mostrarCompra(): void {
    this.goToTop(true);
    this.paso = 11;
    this.armarUrl({ paso: 11 });
    let dataProducts = this.datosArmados.getValue();
    let finalData = this.cargarProductosFinales(dataProducts);
    this.idProductsBuild.set(finalData.map((fd) => fd.product.id_producto));

    this.datosProducto.next(finalData);
  }

  private cargarProductosFinales(datosArmados: DatosArmado): ProductInCompatibility<Product>[] {
    const finalData: Array<ProductInCompatibility<Product>> = [];
    const serviciosArmado: Array<ProductInCompatibility<Product>> =
      this.cargarServiciosArmado(datosArmados);
    const cableFuente: ProductInCompatibility<Product> = this.cargarCableFuente(datosArmados);

    for (const product in datosArmados) {
      if (datosArmados[product].productoSeleccionado?.length) {
        finalData.push(...datosArmados[product].productoSeleccionado);
      }
    }
    if (cableFuente) finalData.push(cableFuente);
    finalData.push(...serviciosArmado);
    return finalData;
  }

  private cargarServiciosArmado(datosArmado: DatosArmado): ProductInCompatibility<Product>[] {
    const serviciosArmado: ProductInCompatibility<Product>[] = [];
    const esPc: boolean = this.validarComponentesObligatorios(datosArmado);
    if (!esPc) return [];
    if (datosArmado.video.productoSeleccionado?.length) {
      serviciosArmado.push(new ProductInCompatibility(this.piramidalService.products.cache[4671]));
    } else {
      serviciosArmado.push(new ProductInCompatibility(this.piramidalService.products.cache[5043]));
    }
    if (
      datosArmado.ccpu.productoSeleccionado.every(
        (cooler) => cooler.product.id_producto != 2093 && cooler.product.id_producto != 2094,
      )
    ) {
      serviciosArmado.push(new ProductInCompatibility(this.piramidalService.products.cache[2615]));
    }
    serviciosArmado.forEach((serv) => (serv.cantidad = 1));
    return serviciosArmado;
  }

  private cargarCableFuente(datosArmado: DatosArmado): ProductInCompatibility<Product> {
    let cableFuente: ProductInCompatibility<Product>;
    const esPc: boolean = this.validarComponentesObligatorios(datosArmado);
    //NO AGREGAR CABLE SI
    //armado no es valido
    //no tiene seleccionada fuente
    //la fuente q selecciono ya incluye cable
    if (
      !esPc ||
      !datosArmado.fuente.productoSeleccionado?.length ||
      datosArmado.fuente?.productoSeleccionado[0]?.product?.$caracteristicas?.incluye_cable
    )
      return null;

    cableFuente = new ProductInCompatibility(this.piramidalService.products.cache[1705]);
    cableFuente.cantidad = 1;

    return cableFuente;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.dialogRefSubscription?.unsubscribe();
  }
}
